import React, { useEffect, useState } from "react";
import { sendCustomEvent } from "../events";
import {
  showTagDetails,
  TagCreateDialog,
  TagDeleteDialog
} from "../folder";
import { isStaticSite } from "../electron-modules";
import { DialogContent, Typography } from "@material-ui/core";
import {
  DenseIcon,
  DenseMenu,
  DraggableDialog,
  LabelCategoryDialog,
  EnhancedTable,
  MenuDivider
} from "../components";
import MenuItem from '@material-ui/core/MenuItem';
import { nanoid } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useAuth, useContextMenu, useDialog, useViewer } from "../hooks";
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import EditIcon from '@material-ui/icons/Edit';
import RoomIcon from "@material-ui/icons/Room";
import ClearIcon from '@material-ui/icons/Clear';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import ToggleOffIcon from '@material-ui/icons/ToggleOff';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import SearchIcon from '@material-ui/icons/Search';
import SyncIcon from '@material-ui/icons/Sync';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import LinkIcon from '@material-ui/icons/Link';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import { useDispatch, useSelector } from "react-redux";
import { CustomURL, selectAllCustomLinks } from "../redux/custom-links-slice";
import { openExternalLink } from "../urls";
import { toast } from "../app";
import { updateTag } from "../redux/assets-slice";
import { useTableState } from "../hooks/use-table-state";
import copyTextToClipboard from "copy-to-clipboard";

export interface LabelContextData {
  labelID: number;
  pointID: number;
  coordinate: number[] | object;
}

export interface ContextData {
  isAerial?: boolean;
  sceneCoord?: number[];
  aerialCoord?: number[];
  labelData?: LabelContextData;
  selectedTags?: string[];
  modelData?: object[];
}

interface ContextMenuItem {
  text: string;
  icon: React.ReactNode;
  seperator?: boolean;
  disabled?: boolean;
  visible?: boolean;
  callback: () => void;
}

interface ContextMenuProps {
  clearTooltipText: () => void;
}

export const ContextMenu = (props: ContextMenuProps) => {
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const contextMenu = useContextMenu();
  const newTagDialog = useDialog();
  const deleteDialog = useDialog();
  const changeCategoryDialog = useDialog();
  const customLinks = useSelector(selectAllCustomLinks);
  const {permissions} = useAuth();

  const [modelProperties, setModelProperties] = useState(null);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [tagCoordinate, setTagCoordinate] = useState([]);

  const {event, contextData={}} = contextMenu.data;

  const {isAerial=false, labelData, selectedTags, modelData,
    sceneCoord, aerialCoord} = contextData as ContextData;

  const multipleCategories  = ((viewer?.getAllCategories().length) > 1);

  const onClose = () => {
    contextMenu.handleClose();
    viewer?.clearCadHighlight();
  };

  const onEnter = () => {
    props.clearTooltipText();
  };

  const listItemSeperator = (visible) => {
    return {
      text: "",
      seperator: true,
      visible
    };
  };

  const constructCustomURL = (customURL: CustomURL, sceneCoord, aerialCoord) => {
    let urlString = customURL.url;

    let position = isAerial
      ? aerialCoord
      : sceneCoord;

    if (position.length === 2) {
      // Add placeholder height to aerial coordinates
      position.push(0);
    }

    urlString = urlString
      .replaceAll('{x}', position[0].toFixed(3))
      .replaceAll('{y}', position[1].toFixed(3))
      .replaceAll('{z}', position[2].toFixed(3));

    const target = customURL.target;
    openExternalLink(urlString, {target});
  };

  const getMenuItems = () => {
    if (!viewer || !contextMenu.open) {
      return [] as ContextMenuItem[];
    }

    let numLabelsSelected = viewer.numLabelsHighlighted();
    let profileActive = viewer.pointProfileActive();

    const labelSelected = labelData
      ? labelData.labelID !== null
      : false;

    const labelPointSelected = labelData
      ? labelData.pointID !== null
      : false;

    const hasLabelCoord = labelData
      ? labelData.coordinate !== null
      : false;

    const numTagsSelected = selectedTags
      ? selectedTags.length
      : 0;

    const hasModelData = modelData
      ? modelData.length > 0
      : false;

    const enableChangeCategory = multipleCategories && (numLabelsSelected > 0);

    const hasSceneCoord = !!sceneCoord;
    const hasAerialCoord = !!aerialCoord;

    const noTagSelected = numTagsSelected === 0;
    const oneTagSelected = numTagsSelected === 1;

    const pointProfileActions = [
      {
        text: t("context-menu.profile"),
        icon: <TrendingUpIcon fontSize="small"/>,
        disabled: !hasSceneCoord,
        visible: !isAerial && !profileActive,
        callback: () => {
          viewer.pointProfileStart(sceneCoord);
        }
      },
      {
        text: t("context-menu.profile-end"),
        icon: <TrendingDownIcon fontSize="small"/>,
        disabled: !hasSceneCoord,
        visible: !isAerial && profileActive,
        callback: () => {
          viewer.pointProfileEnd();
        }
      },
      {
        text: t("context-menu.profile-cancel"),
        icon: <ClearIcon fontSize="small"/>,
        visible: !isAerial && profileActive,
        callback: () => {
          viewer.pointProfileCancel();
        }
      },
    ] as ContextMenuItem[];;

    const tagActions = [
      {
        text: t("context-menu.new-tag"),
        icon: <RoomIcon fontSize="small"/>,
        visible: permissions.create
          && (hasSceneCoord || hasAerialCoord)
          && noTagSelected,
        callback: () => {
          setTagCoordinate(sceneCoord || aerialCoord);
          newTagDialog.handleOpen();
        }
      },
      {
        text: t("context-menu.new-tag"),
        icon: <RoomIcon fontSize="small"/>,
        visible: permissions.create
          && viewer.isImageOnly
          && !hasSceneCoord
          && !hasAerialCoord
          && noTagSelected,
        callback: async () => {
          const coordinate = await viewer.getMultiImageCoordinate(event);
          if (!coordinate) return;

          const tagCoordinate = coordinate
            .toDataProjection()
            .toArray();

          setTagCoordinate(tagCoordinate);
          newTagDialog.handleOpen();
        }
      },
      {
        text: t('tags.move_to_tag'),
        icon: <ZoomInIcon fontSize="small"/>,
        visible: isAerial && oneTagSelected,
        callback: () => {
          // Jump to nearest camera position
          viewer.jumpToTag(selectedTags[0]);
        }
      },
      {
        text: t("context-menu.copy-tag"),
        icon: <LinkIcon fontSize="small"/>,
        visible: isStaticSite && oneTagSelected,
        callback: () => {
          // Copy url to clipboard
          const url = new URL(window.location as any);
          url.hash = `tag=${selectedTags[0]}`;
          copyTextToClipboard(url.href);
          toast.success(t("toast.copied-to-clipboard"));
        }
      },
      {
        text: t("context-menu.update-tag-view"),
        icon: <SyncIcon fontSize="small"/>,
        visible: permissions.edit && oneTagSelected,
        callback: () => {
          dispatch(updateTag({
            tagID: selectedTags[0],
            sceneState: viewer.sceneState
          }));

          toast.success(t("toast.updated-tag-view"));
        }
      },
      {
        text: t("context-menu.edit-tag"),
        icon: <EditIcon fontSize="small"/>,
        visible: permissions.edit && oneTagSelected,
        callback: () => {
          showTagDetails(selectedTags[0], true);
        }
      },
      {
        text: t("context-menu.delete-tags", {
          count: numTagsSelected
        }),
        icon: <DeleteIcon fontSize="small"/>,
        visible: permissions.delete && !noTagSelected,
        callback: () => {
          setTagsToDelete(selectedTags);
          deleteDialog.handleOpen();
        }
      }
    ] as ContextMenuItem[];

    const orbitModeActions = [
      {
        text: viewer.orbitState
          ? t("context-menu.disable-3d-mode")
          : t("context-menu.enable-3d-mode"),
        icon: viewer.orbitState
          ? <ToggleOffIcon fontSize="small"/>
          : <ToggleOnIcon fontSize="small"/>,
        visible: !isAerial,
        callback: () => {
          viewer.toggleOrbitState();
        }
      },
      {
        text: t("context-menu.reset-3d-mode"),
        icon: <RotateLeftIcon fontSize="small"/>,
        disabled: !viewer.orbitState,
        visible: !isAerial,
        callback: () => {
          viewer.resetCamera();
        }
      },
    ] as ContextMenuItem[];

    const modelActions = [
      {
        text: t("context-menu.model-properties"),
        icon: <InfoIcon fontSize="small"/>,
        visible: hasModelData,
        callback: () => {
          setModelProperties(modelData);
        }
      }
    ] as ContextMenuItem[];

    const labelActions = [
      {
        text: t("context-menu.delete-label", {
          count: numLabelsSelected
        }),
        icon: <DeleteIcon fontSize="small"/>,
        disabled: viewer.anyLabelSelected(),
        callback: () => {
          viewer.deleteHighlighted();
        }
      },
      {
        text: t("context-menu.delete-point"),
        icon: <DeleteIcon fontSize="small"/>,
        disabled: !labelPointSelected,
        callback: () => {
          viewer.deleteLabelPoint(labelData.pointID);
        }
      },
      {
        text: t("context-menu.unselect-label", {
          count: numLabelsSelected
        }),
        icon: <ClearIcon fontSize="small"/>,
        disabled: viewer.anyLabelSelected(),
        callback: () => {
          viewer.unHighlightAll();
        }
      },
      {
        text: t("context-menu.insert-point"),
        icon: <AddCircleOutlineIcon fontSize="small"/>,
        disabled: !hasLabelCoord || labelPointSelected,
        callback: () => {
          viewer.insertLabelPoint(
            labelData.labelID,
            labelData.coordinate
          );
        }
      },
      {
        text: t("context-menu.edit-point"),
        icon: <EditIcon fontSize="small"/>,
        disabled: !hasLabelCoord || labelPointSelected,
        callback: () => {
          viewer.editLabel(
            labelData.labelID,
            labelData.coordinate
          );
        }
      },
      {
        text: t("context-menu.find-in-list"),
        icon: <SearchIcon fontSize="small"/>,
        disabled: !labelSelected,
        callback: () => {
          viewer.moveToLabel(labelData.labelID);
        }
      },
      {
        text: t("context-menu.change-category"),
        icon: <CompareArrowsIcon fontSize="small"/>,
        disabled: !enableChangeCategory,
        callback: () => {
          changeCategoryDialog.handleOpen();
        }
      },
    ] as ContextMenuItem[];

    const customUserActions = customLinks
      .filter(values => {
        return isAerial
          ? values.showInAerial
          : values.showInScene;
      })
      .map(values => {
        return {
          text: values.name,
          icon: <LinkIcon fontSize="small"/>,
          disabled: !hasSceneCoord && !hasAerialCoord,
          callback: () => {
            constructCustomURL(values, sceneCoord, aerialCoord);
          }
        };
      }) as ContextMenuItem[];

    if (numLabelsSelected > 0) {
      // Labels are selected
      return [...labelActions];
    }

    const hasCustom = customUserActions.length > 0;

    return [
      ...tagActions,
      listItemSeperator(hasModelData),
      ...modelActions,
      listItemSeperator(!isAerial),
      ...orbitModeActions,
      listItemSeperator(!isAerial),
      ...pointProfileActions,
      listItemSeperator(hasCustom),
      ...customUserActions
    ] as ContextMenuItem[];
  };

  /** Context menu event listener */
  useEffect(() => {
    const callback = (menuEvent) => {
      const {event} = menuEvent.detail;
      contextMenu.handleOpen(event, menuEvent.detail);
    };

    document.addEventListener("context-menu", callback);

    return () => {
      document.removeEventListener("context-menu", callback);
    };
  }, []);

  let options = getMenuItems();

  options = options.filter(item => {
    return item.hasOwnProperty("visible")
      ? item.visible
      : true;
  });

  // Make sure a seperator isn't the first element
  if ((options.length > 0) && options[0].seperator) {
    options = options.slice(1);
  }

  return (
    <React.Fragment>
      <DenseMenu
        open={contextMenu.open && options.length > 0}
        onClose={onClose}
        onEnter={onEnter}
        anchorPosition={contextMenu.anchorPosition}
      >
        {options.map((option) => {
          if (option.seperator) {
            return <MenuDivider key={nanoid()}/>;
          }

          return (
            <MenuItem
              key={nanoid()}
              disabled={option.disabled}
              onClick={() => {
                onClose();
                option.callback();
              }}
            >
              <DenseIcon>{option.icon
                ? option.icon
                : <InfoIcon fontSize="small"/>
              }</DenseIcon>

              <Typography variant="inherit">
                {option.text}
              </Typography>

            </MenuItem>
          );
        })}
      </DenseMenu>

      {/* New Tag Dialog */}
      <TagCreateDialog
        open={newTagDialog.open}
        onClose={newTagDialog.handleClose}
        coordinate={tagCoordinate}
      />

      {/* Delete Tag Dialog */}
      <TagDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        toDelete={tagsToDelete}
      />

      <ModelProperties
        data={modelProperties}
        setData={setModelProperties}
      />

      {/* Change Category Dialog */}
      <LabelCategoryDialog
        open={changeCategoryDialog.open}
        onClose={changeCategoryDialog.handleClose}
      />

    </React.Fragment>
  );
};



interface ModelPropertiesProps {
  data: any[]
  setData: (data) => void;
}

const ModelProperties = (props: ModelPropertiesProps) => {
  const {data, setData} = props;

  const {t} = useTranslation();
  const tableState = useTableState();

  const open = data ? data.length > 0 : false;

  const onClose = () => {
    setData(null);
  };

  const getRows = () => {
    if (!data) return [];

    return data.map((property, index) => {
      return {
        id: `PropertyTableRow_${index}`,
        ...property
      };
    });
  };

  const columns = [
    {
      id: 'property',
      label: t("model-properties.property")
    },
    {
      id: 'value',
      label: t("model-properties.value"),
      wordBreak: true
    }
  ];

  const rows = getRows();

  return (
    <DraggableDialog
      open={open}
      title={t("model-properties.title")}
      onClose={onClose}
    >
      <DialogContent>
        <EnhancedTable
          state={tableState}
          columns={columns}
          rows={rows}
        />
      </DialogContent>
    </DraggableDialog>
  );
};

export const showContextMenu = (event, contextData = {} as ContextData) => {
  event.preventDefault();
  sendCustomEvent("context-menu", {event, contextData});
};